import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart2, DollarSign, TrendingUp, Map, Layers,
  Filter, Download, Calendar, ChevronDown, 
  Users, Package, RefreshCw, Clock
} from 'lucide-react';

// Import sub-components
import SalesAnalysis from './analytics/SalesAnalysis';
import PerformanceComparison from './analytics/PerformanceComparison';
import TopPerformers from './analytics/TopPerformers';
import HourlySales from './analytics/HourlySales';
import ProductMix from './analytics/ProductMix';
import RefundTracking from './analytics/RefundTracking';
import ForecastProjections from './analytics/ForecastProjections';
import CustomReportBuilder from './analytics/CustomReportBuilder';

// Analytics sections definition
const analyticsSections = [
  { id: 'sales', name: 'Sales Analysis', icon: BarChart2, color: 'blue' },
  { id: 'comparison', name: 'Performance Comparison', icon: TrendingUp, color: 'green' },
  { id: 'top-performers', name: 'Top & Least Performers', icon: Users, color: 'purple' },
  { id: 'hourly', name: 'Hourly & Peak Sales', icon: Clock, color: 'amber' },
  { id: 'products', name: 'Product Mix & Insights', icon: Package, color: 'pink' },
  { id: 'refunds', name: 'Refund & Discount Tracking', icon: RefreshCw, color: 'red' },
  { id: 'forecast', name: 'Forecast & Projections', icon: TrendingUp, color: 'indigo' },
  { id: 'reports', name: 'Custom Reports Builder', icon: Layers, color: 'teal' },
];

// Filter types
const filterOptions = {
  storeTypes: [
    { id: 'all', name: 'All Types' },
    { id: 'cafe', name: 'Café' },
    { id: 'cafe-dt', name: 'Café+DT' },
    { id: 'dt', name: 'Drive-thru' },
    { id: 'kiosk', name: 'Kiosk' },
  ],
  regions: [
    { id: 'all', name: 'All Regions' },
    { id: 'riyadh', name: 'Riyadh Region' },
    { id: 'eastern', name: 'Eastern Province' },
    { id: 'makkah', name: 'Makkah Region' },
    { id: 'asir', name: 'Asir Region' },
    { id: 'tabuk', name: 'Tabuk Region' },
  ],
  dateRanges: [
    { id: 'today', name: 'Today' },
    { id: 'yesterday', name: 'Yesterday' },
    { id: '7d', name: 'Last 7 Days' },
    { id: '30d', name: 'Last 30 Days' },
    { id: 'mtd', name: 'Month to Date' },
    { id: 'ytd', name: 'Year to Date' },
    { id: 'custom', name: 'Custom Range' },
  ]
};

const AnalyticsTab = ({ timeRange }) => {
  const [activeSection, setActiveSection] = useState('sales');
  const [filters, setFilters] = useState({
    storeType: 'all',
    region: 'all',
    dateRange: '30d',
    customDateRange: {
      startDate: null,
      endDate: null,
    },
    includeTax: false, // Toggle for VAT inclusion
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);

  // Handler for filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Helper for getting active section component
  const renderActiveSection = () => {
    switch(activeSection) {
      case 'sales':
        return <SalesAnalysis filters={filters} />;
      case 'comparison':
        return <PerformanceComparison filters={filters} />;
      case 'top-performers':
        return <TopPerformers filters={filters} />;
      case 'hourly':
        return <HourlySales filters={filters} />;
      case 'products':
        return <ProductMix filters={filters} />;
      case 'refunds':
        return <RefundTracking filters={filters} />;
      case 'forecast':
        return <ForecastProjections filters={filters} />;
      case 'reports':
        return <CustomReportBuilder filters={filters} />;
      default:
        return <SalesAnalysis filters={filters} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Analytics Dashboard</h2>
            
            {/* VAT Toggle */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Net Sales</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={filters.includeTax}
                  onChange={() => handleFilterChange('includeTax', !filters.includeTax)}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
              <span className="text-sm text-gray-600 dark:text-gray-400">Inc. VAT</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Date Range Selector */}
            <div className="relative">
              <button 
                onClick={() => setIsDateRangeOpen(!isDateRangeOpen)}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg py-2 px-3 text-sm font-medium shadow-sm"
              >
                <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span>{filterOptions.dateRanges.find(dr => dr.id === filters.dateRange)?.name || 'Date Range'}</span>
                <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
              
              {isDateRangeOpen && (
                <div className="absolute top-full right-0 mt-2 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-56">
                  <div className="space-y-2">
                    {filterOptions.dateRanges.map(range => (
                      <button
                        key={range.id}
                        onClick={() => {
                          handleFilterChange('dateRange', range.id);
                          setIsDateRangeOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                          filters.dateRange === range.id
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                        }`}
                      >
                        {range.name}
                      </button>
                    ))}
                  </div>
                  
                  {filters.dateRange === 'custom' && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3 space-y-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                        <input 
                          type="date"
                          value={filters.customDateRange.startDate || ''}
                          onChange={(e) => handleFilterChange('customDateRange', {
                            ...filters.customDateRange,
                            startDate: e.target.value
                          })}
                          className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                        <input 
                          type="date"
                          value={filters.customDateRange.endDate || ''}
                          onChange={(e) => handleFilterChange('customDateRange', {
                            ...filters.customDateRange,
                            endDate: e.target.value
                          })}
                          className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Filter Button */}
            <div className="relative">
              <button 
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg py-2 px-3 text-sm font-medium shadow-sm"
              >
                <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span>Filters</span>
                <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
              
              {isFilterOpen && (
                <div className="absolute top-full right-0 mt-2 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-64">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Store Type</h3>
                      <div className="space-y-2">
                        {filterOptions.storeTypes.map(type => (
                          <button
                            key={type.id}
                            onClick={() => handleFilterChange('storeType', type.id)}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                              filters.storeType === type.id
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                            }`}
                          >
                            {type.name}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Region</h3>
                      <div className="space-y-2">
                        {filterOptions.regions.map(region => (
                          <button
                            key={region.id}
                            onClick={() => handleFilterChange('region', region.id)}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                              filters.region === region.id
                                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                            }`}
                          >
                            {region.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Export Button */}
            <button className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg py-2 px-3 text-sm font-medium shadow-sm">
              <Download className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Analytics Section Navigation - Compact Grid Layout */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
          {analyticsSections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-1.5 py-1.5 px-2 rounded-md transition-colors ${
                activeSection === section.id
                  ? `bg-${section.color}-50 dark:bg-${section.color}-900/20 border-${section.color}-200 dark:border-${section.color}-800/30 border`
                  : 'bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700/50'
              }`}
            >
              <section.icon className={`h-3.5 w-3.5 ${
                activeSection === section.id
                  ? `text-${section.color}-500 dark:text-${section.color}-400`
                  : 'text-gray-500 dark:text-gray-400'
              }`} />
              <span className={`text-xs font-medium truncate ${
                activeSection === section.id
                  ? `text-${section.color}-700 dark:text-${section.color}-300`
                  : 'text-gray-700 dark:text-gray-300'
              }`}>
                {section.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Active Section */}
      <motion.div
        key={activeSection}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3 }}
      >
        {renderActiveSection()}
      </motion.div>
    </div>
  );
};

export default AnalyticsTab; 